import hotelDetailsDAO from '@niarab2c/frontend-commons/src/daos/hotelDetailsDAO';
import { AddonAvailabilityPayload, AddonAvailabilityResponse } from '@niarab2c/frontend-commons/src/types/addon';
import { EntitiesDataProps, GetHotelAvailResponse, HotelResult, HotelsAvailabilityPayload, OriginalHotelResult, RoomRate } from '@niarab2c/frontend-commons/src/types/hotels';
import callApi from '@niarab2c/frontend-commons/src/util/callApi';
import { Storefront } from '@niarab2c/otabuilder-crudmodel';
import { AnyAction, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addMonths, endOfMonth, isAfter, isBefore, isSameDay, parseISO } from 'date-fns';
import _differenceBy from 'lodash/differenceBy';
import _flatMap from 'lodash/flatMap';
import _groupBy from 'lodash/groupBy';
import _isEqual from 'lodash/isEqual';
import _map from 'lodash/map';
import _pick from 'lodash/pick';
import _uniqBy from 'lodash/uniqBy';
import { prepareHotelResult } from '../../../util/hotels/prepareHotelResult';
import { CompleteRootState } from '../../base';
import * as HotelPagination from '../hotel/hotelPagination';
import { HotelPaginationState } from '../hotel/hotelPagination';
export * as HotelPagination from '../hotel/hotelPagination';
export type LongStayState = {
  longStayPagination: HotelPaginationState;
  longStaySearch: LongStaySearchState;
};
export type DestinationCriteria = {
  propertyId?: string;
  contentType?: 'property' | 'region';
  cityIds?: Array<string>;
  hotelIds?: Array<string>;
  mapBounds?: Record<'s' | 'w' | 'n' | 'e', number>;
  previousMapBounds?: Record<'s' | 'w' | 'n' | 'e', number>[];
};
export type LongStayCriteria = {
  startDate: string;
  endDate: string;
  /**
   * Usado para acessar valores do objeto.
   */
  destinations?: DestinationCriteria;
  destinationName?: string | Array<string>;
  promoCode?: string;
  couponCode?: string;
  credentialIds?: string[];
  availRatesOnly?: boolean;
  roomRateIds?: string[];
  clientId?: string;
  clientName?: string;
  enablePromoCode?: boolean;
  isThirdPartyReservation?: boolean;
  previousOrderId?: string;
  periodMode?: 'date' | 'month';
  ratePlanId?: string | number;
  roomTypeId?: string;
} &
/**
* Usado para preencher o destino no CriteriaComponent a partir dos valores da url
*/
DestinationCriteria;
export type LongStayResult = HotelResult;
export type LongStayResultFilterKey = 'hotelAmenities' | 'neighborhood' | 'hotelId';
export type LongStayRoomRateFilterKey = 'bedroomCount' | 'apartmentSize';
export type LongStayFilterKeys = LongStayResultFilterKey | LongStayRoomRateFilterKey;
export type LongStayFilter = {
  priceMin?: number;
  priceMax?: number;
  hotelId?: string;
} & Partial<Record<LongStayFilterKeys, any[]>>;
export type LongStaySortOption = 'PRICE_ASC' | 'PRICE_DESC';
export type LongStaySearchState = {
  searchId?: string;
  mapSearchId?: string;
  criteria?: LongStayCriteria;
  searchInProgress?: boolean;
  mapSearchInProgress?: boolean;
  results?: LongStayResult[];
  mapResults?: LongStayResult[];
  mapBounds?: Record<'s' | 'w' | 'n' | 'e', number>;
  previousMapBounds?: Record<'s' | 'w' | 'n' | 'e', number>[];
  filter?: LongStayFilter;
  errorMessage?: string;
  regionLocation?: EntitiesDataProps['regions'][number];
};
const getAddonAvailability = async (propertyId: string, roomRate: RoomRate, criteriaForm: LongStayCriteria, storefront: Storefront, clientId?: string): Promise<AddonAvailabilityResponse> => {
  const addonPayload: AddonAvailabilityPayload = {
    propertyId: propertyId,
    roomTypeId: roomRate?.roomType?.id,
    clientId: clientId ?? criteriaForm?.clientId,
    startDate: criteriaForm?.startDate,
    endDate: criteriaForm?.endDate,
    landingPageId: storefront?.landingPageId
  };
  return callApi('niara-spear-booking-addons', `/addons/availability`, 'post', {
    body: addonPayload
  });
};
const prepareLongStayResult = async (hotel: OriginalHotelResult, criteriaForm: LongStayCriteria, b2c?: boolean, storefront?: Storefront, entitiesData?: EntitiesDataProps): Promise<LongStayResult> => {
  const _hotelDetails = await hotelDetailsDAO.findById(hotel?.hotel?.id, undefined, undefined, hotel?.hotel?.detailsUrl);
  const roomRates = hotel?.roomRates.filter(rr => rr.addons?.length > 0);
  return {
    ...prepareHotelResult({
      ...hotel,
      roomRates
    }, {
      _hotelDetails
    } as any, {
      occupancy: {
        adults: 1
      },
      engineRuleVersion: '1',
      selfBooking: false,
      b2c,
      entitiesData
    })
  };
};
export const search = createAsyncThunk<{
  results: Array<LongStayResult>;
  searchId: string;
  mapSearchId: string;
  longStayPagination: HotelPaginationState;
  criteria: LongStayCriteria;
  regionLocation: LongStaySearchState['regionLocation'];
}, {
  criteriaForm: LongStayCriteria;
  isMapSearch?: boolean;
  searchId?: string;
  mapSearchId?: string;
  mapBounds?: LongStaySearchState['mapBounds'];
}, {
  state: CompleteRootState;
  rejectValue: {
    searchId: string;
    mapSearchId: string;
    errorMessage: string;
  };
}>('longStay/search', async ({
  criteriaForm,
  isMapSearch,
  searchId,
  mapSearchId,
  mapBounds
}, {
  getState,
  rejectWithValue,
  dispatch
}) => {
  const rootState = getState();
  const storefront = rootState.storefrontConfig?.storefront;
  const landingPageLocator = rootState.storefrontConfig?.storefront?.locator;
  const purchaseSessionId = rootState.shoppingCart?.purchaseSessionId;
  const previousMapBounds = rootState?.longStay?.longStaySearch?.previousMapBounds ?? [];
  const b2c = rootState?.authentication?.b2c;
  const criteria: HotelsAvailabilityPayload['criteria'] = {
    ..._pick(criteriaForm, 'availRatesOnly', 'promoCode', 'limit', 'offset', 'credentialIds', 'bestOnly', 'distributionIds', 'roomRateIds', 'destinations', 'clientName', 'clientId', 'roomTypeId', 'ratePlanId'),
    searchId,
    time: {
      endDate: criteriaForm.endDate,
      startDate: criteriaForm.startDate
    },
    destinationName: !criteriaForm.destinationName ? undefined : typeof criteriaForm.destinationName == 'string' ? criteriaForm.destinationName : criteriaForm.destinationName.join(', '),
    landingPage: landingPageLocator,
    occupancy: {
      adults: 1,
      quantity: 1
    },
    destinations: {
      ...criteriaForm.destinations,
      mapBounds: isMapSearch ? mapBounds : undefined,
      previousMapBounds: isMapSearch ? previousMapBounds : undefined
    }
  };
  if (isMapSearch) {
    dispatch(addMapBounds(mapBounds));
  }
  const apipath = landingPageLocator ? `/otabuilder/${landingPageLocator}/hotels/availability` : '/hotels/availability';
  const payload: HotelsAvailabilityPayload = {
    isThirdPartyReservation: criteriaForm?.isThirdPartyReservation || false,
    engineRuleVersion: '1',
    couponCode: criteriaForm?.couponCode,
    criteria: {
      ...criteria
    },
    purchaseSessionId,
    buyer_personId: null,
    isLongStay: true
  };
  return await callApi('niara-spear-booking', apipath, 'post', {
    body: payload
  }).then(async (response: GetHotelAvailResponse) => {
    const results = (await Promise.all(response?.hotels?.map(async hotel => {
      return prepareLongStayResult(hotel, criteriaForm, b2c, storefront, response?.entitiesData);
    }))) as Array<LongStayResult>;
    const hotelResultsById = results?.reduce((acc, result) => {
      acc[result.hotel.id] = result;
      return acc;
    }, {} as Record<string, LongStayResult>);
    const longStayPagination = isMapSearch ? rootState.longStay?.longStayPagination : HotelPagination.reducer(rootState.longStay?.longStayPagination, HotelPagination.setResultsLongStay({
      results: hotelResultsById,
      hotelSearch: {
        criteria: criteriaForm,
        results
      }
    }));
    let periodMode: LongStayCriteria['periodMode'] = 'date';
    try {
      const checkinParsed = parseISO(criteriaForm?.startDate);
      const checkoutParsed = parseISO(criteriaForm?.endDate);
      const isCheckinLastDay = isSameDay(checkinParsed, endOfMonth(checkinParsed));
      const isCheckoutLastDay = isSameDay(checkoutParsed, endOfMonth(checkoutParsed));
      const isCheckoutAfterCheckin = isAfter(checkoutParsed, checkinParsed);
      let currentMonth = checkinParsed;
      let allMonthsFull = true;
      while (isBefore(currentMonth, checkoutParsed)) {
        const lastDayOfCurrentMonth = endOfMonth(currentMonth);
        if (isBefore(checkoutParsed, lastDayOfCurrentMonth)) {
          allMonthsFull = false;
          break;
        }
        currentMonth = addMonths(currentMonth, 1);
      }
      if (checkinParsed.getDate() === checkoutParsed.getDate() || isCheckinLastDay && isCheckoutLastDay && isCheckoutAfterCheckin && allMonthsFull) {
        periodMode = 'month';
      }
    } catch (_) {
      // Intentionally ignored
    }
    const searchLocation = isMapSearch ? rootState?.longStay?.longStaySearch?.regionLocation : criteriaForm?.destinations?.contentType === 'region' ? response?.entitiesData?.regions?.[criteriaForm?.destinations?.propertyId] : {
      id: results?.[0]?.hotel?.propertyId,
      name: results?.[0]?.hotel?.name,
      coordinates: {
        lat: String(results?.[0]?.hotel?.position?.latitude),
        lon: String(results?.[0]?.hotel?.position?.longitude)
      }
    };
    return {
      searchId,
      mapSearchId,
      results,
      longStayPagination,
      criteria: {
        ...criteriaForm,
        periodMode
      },
      regionLocation: searchLocation
    };
  }).catch(error => {
    // eslint-disable-next-line no-console
    console.error(error);
    return rejectWithValue({
      errorMessage: error?.message || error?.errorMessage || typeof error == 'string' && error || "Erro desconhecido",
      searchId,
      mapSearchId
    });
  });
});
const oneResultByRoomRate = (results: LongStayResult[]) => {
  return _flatMap(results, hotel => hotel.roomRates.map(roomRate => ({
    ...hotel,
    roomRates: [roomRate]
  })));
};
const initialState: LongStayState = {
  longStayPagination: {
    ...HotelPagination.default(undefined, {
      type: 'IGNORE'
    }),
    pageSize: 6
  },
  longStaySearch: {
    searchId: undefined,
    mapSearchId: undefined,
    criteria: undefined,
    searchInProgress: false,
    mapSearchInProgress: false,
    results: [],
    mapResults: [],
    mapBounds: undefined,
    previousMapBounds: [],
    filter: undefined,
    errorMessage: undefined
  }
};
const longStaySlice = createSlice({
  name: 'longStay',
  initialState,
  reducers: {
    addMapBounds: (state, action: PayloadAction<LongStaySearchState['previousMapBounds'][number]>) => {
      if (state.longStaySearch.previousMapBounds.some(bounds => _isEqual(bounds, action.payload))) return state;
      state.longStaySearch.previousMapBounds = [...state.longStaySearch.previousMapBounds, action.payload];
    }
  },
  extraReducers: builder => {
    builder.addCase(search.pending, (state, action) => {
      const isMapSearch = action.meta.arg.isMapSearch;
      const isValidSearch = isMapSearch ? !state.longStaySearch.mapSearchId || state.longStaySearch.mapSearchId == action.meta.arg.mapSearchId : !state.longStaySearch.searchId || state.longStaySearch.searchId == action.meta.arg.searchId;
      if (!isValidSearch) return state;
      return {
        ...state,
        longStaySearch: {
          ...state.longStaySearch,
          results: isMapSearch ? state.longStaySearch.results : [],
          // só limpa se de fato for novo criterio de busca. No caso, quando não for mapSearch
          mapResults: isMapSearch ? state.longStaySearch.mapResults : [],
          // só limpa se de fato for novo criterio de busca. No caso, quando não for mapSearch
          previousMapBounds: isMapSearch ? state.longStaySearch.previousMapBounds : [],
          // só limpa se de fato for novo criterio de busca. No caso, quando não for mapSearch
          searchInProgress: isMapSearch ? state.longStaySearch.searchInProgress : true,
          mapSearchInProgress: isMapSearch ? true : state.longStaySearch.mapSearchInProgress,
          searchId: isMapSearch ? state.longStaySearch.searchId : action.meta.arg.searchId,
          mapSearchId: isMapSearch ? action.meta.arg.mapSearchId : state.longStaySearch.mapSearchId
        }
      };
    }).addCase(search.fulfilled, (state, action) => {
      const isMapSearch = action.meta.arg.isMapSearch;
      const isValidSearch = isMapSearch ? state.longStaySearch.mapSearchId === action.payload.mapSearchId : state.longStaySearch.searchId === action.payload.searchId;
      if (!isValidSearch) return state;
      if (isMapSearch) {
        // junta o mapResults atual com o resultado da nova busca
        // além disso, mantém no mapResults apenas resultados de hoteis que não estão no results
        const mergedResults = _map(_groupBy([...state.longStaySearch.mapResults, ...action.payload.results], 'hotel.id'), hotels => {
          if (hotels.length === 1) return hotels[0];
          return {
            ...hotels[0],
            roomRates: _uniqBy(_flatMap(hotels, 'roomRates'), 'id')
          };
        });
        const uniqueMapResults = _differenceBy(oneResultByRoomRate(mergedResults), state.longStaySearch.results, 'hotel.id');
        return {
          ...state,
          longStaySearch: {
            ...state.longStaySearch,
            mapSearchInProgress: false,
            mapResults: uniqueMapResults,
            mapSearchId: undefined
          }
        };
      }
      const newResults = oneResultByRoomRate(action.payload.results);
      return {
        ...state,
        longStaySearch: {
          ...state.longStaySearch,
          searchInProgress: false,
          results: newResults,
          mapResults: state.longStaySearch.mapResults?.filter(item => !newResults?.some(newItem => newItem?.hotel?.id === item?.hotel?.id)),
          criteria: action.payload.criteria,
          regionLocation: action.payload.regionLocation,
          searchId: undefined
        },
        longStayPagination: action.payload.longStayPagination
      };
    }).addCase(search.rejected, (state, action) => {
      const isMapSearch = action.meta.arg.isMapSearch;
      const isValidSearch = isMapSearch ? state.longStaySearch.mapSearchId === action.payload.mapSearchId : state.longStaySearch.searchId === action.payload.searchId;
      if (!isValidSearch) return state;
      return {
        ...state,
        longStaySearch: {
          ...state.longStaySearch,
          searchInProgress: isMapSearch ? state.longStaySearch.searchInProgress : false,
          mapSearchInProgress: isMapSearch ? false : state.longStaySearch.mapSearchInProgress,
          searchId: isMapSearch ? state.longStaySearch.searchInProgress : undefined,
          mapSearchId: isMapSearch ? undefined : state.longStaySearch.mapSearchInProgress,
          errorMessage: action.payload?.errorMessage
        }
      };
    }).addMatcher((action: AnyAction) => action.type.startsWith('hotelPagination'), (state, action) => {
      state.longStayPagination = HotelPagination.reducer(state.longStayPagination, action);
    });
  }
});
export const {
  addMapBounds
} = longStaySlice.actions;
export default longStaySlice.reducer;